// FormBuilderPropertiesPanel — the builder's right "Properties" column, shown
// while a canvas field OR the active page is selected: shell (header + type
// identity) over the Basic options accordion (live edits into the element's
// basicProperties) + the Advanced accordion (rows gated per group — a page
// carries validation/layout/others, no tickets/media).

import { useTranslation } from 'react-i18next';
import { Icon, IconButton, type MentionOption } from '@/components/common';
import { FIELD_LIBRARY } from './utils/fieldLibrary';
import type { ChoiceOption, ElementProperties } from './utils/formElementDefaults';
import FormBuilderBasicOptions from './FormBuilderBasicOptions';
import FormBuilderAdvancedOptions from './FormBuilderAdvancedOptions';
import type { BindableField, BuilderField, BuilderPage, GeofenceGate } from './utils/builderModel';

interface FormBuilderPropertiesPanelProps {
  /** The selected canvas element the panel describes — a field or the page. */
  field: BuilderField | BuilderPage;
  /** Replace the selected element's properties (immutable update). */
  onChange: (properties: ElementProperties) => void;
  /** A response set was applied to the selected checkList — replaces its
   *  optionList and kills its conditional-logic rules. */
  onApplyResponseSet?: (options: ChoiceOption[]) => void;
  /** Close the panel (deselect). */
  onClose: () => void;
  /** Fields a dataSource binding can point at (whole-form list). */
  bindableFields: BindableField[];
  /** Geofence gating for the selected location field (whole-form check). */
  geofenceGate?: GeofenceGate;
  /** Form-field mention options for the watermark text (whole-form list). */
  watermarkMentions?: MentionOption[];
}

const FormBuilderPropertiesPanel = ({
  field,
  onChange,
  onApplyResponseSet,
  onClose,
  bindableFields,
  geofenceGate,
  watermarkMentions,
}: FormBuilderPropertiesPanelProps) => {
  const { t } = useTranslation();
  const isPage = field.elementType === 'page';
  const entry = FIELD_LIBRARY.find((f) => f.type === field.elementType);

  return (
    <aside className='form-builder-props' aria-label={t('forms_builder_props_title')}>
      <div className='form-builder-props__head'>
        <Icon name='sliders-horizontal' size={16} />
        <h2 className='form-builder-props__title'>{t('forms_builder_props_title')}</h2>
        <IconButton
          icon='x'
          variant='ghost'
          size='sm'
          aria-label={t('forms_builder_props_close')}
          onClick={onClose}
        />
      </div>

      <div className='form-builder-props__identity'>
        <span className='form-builder-props__tile'>
          <Icon name={isPage ? 'file' : (entry?.icon ?? 'type')} size={16} />
        </span>
        <div className='form-builder-props__identity-text'>
          <span className='form-builder-props__name'>
            {isPage ? t('forms_builder_props_page') : entry ? t(entry.labelKey) : field.elementType}
          </span>
          {isPage ? (
            <span className='form-builder-props__desc'>{t('forms_builder_props_page_desc')}</span>
          ) : (
            entry && <span className='form-builder-props__desc'>{t(entry.descKey)}</span>
          )}
        </div>
      </div>

      {/* keys reset accordion/toggle state when the selection changes */}
      <FormBuilderBasicOptions
        key={field.id}
        field={field}
        onChange={onChange}
        onApplyResponseSet={onApplyResponseSet}
        bindableFields={bindableFields}
      />
      <FormBuilderAdvancedOptions
        key={`${field.id}-adv`}
        field={field}
        onChange={onChange}
        geofenceGate={geofenceGate}
        watermarkMentions={watermarkMentions}
      />
    </aside>
  );
};

export default FormBuilderPropertiesPanel;
